// Lightweight in-app telemetry: errors + perf + render alerts.
// All events are buffered then POSTed to the `ingest-metrics` edge function.

type Kind = "error" | "perf" | "render";

interface Event {
  kind: Kind;
  name: string;
  route?: string | null;
  value?: number | null;
  meta?: Record<string, unknown>;
}

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string | undefined;
const ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
const APP_VERSION = ((import.meta as any).env?.VITE_APP_VERSION as string | undefined) ?? null;

const ENABLED = (() => {
  if (typeof window === "undefined") return false;
  if ((import.meta as any).env?.DEV) {
    return localStorage.getItem("telemetry") === "1";
  }
  return true;
})();

const buffer: Event[] = [];
const MAX_BUFFER = 50;
let flushTimer: number | null = null;
let accountId: string | null = null;

export function setTelemetryAccount(id: string | null) {
  accountId = id;
}

function shouldDrop(e: Event): boolean {
  if (e.kind === "perf" && e.name === "inp" && (e.value ?? 0) < 100) {
    // Sample 10% of "fast" INP
    return Math.random() > 0.1;
  }
  return false;
}

export function track(kind: Kind, name: string, opts: Omit<Event, "kind" | "name"> = {}) {
  if (!ENABLED) return;
  const conn: any = (navigator as any).connection;
  if (conn?.saveData) return;

  const evt: Event = {
    kind,
    name,
    route: opts.route ?? (typeof location !== "undefined" ? location.pathname : null),
    value: opts.value ?? null,
    meta: opts.meta ?? {},
  };
  if (shouldDrop(evt)) return;

  buffer.push(evt);
  if (buffer.length >= MAX_BUFFER) {
    flush();
  } else if (flushTimer == null) {
    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      flush();
    }, 5000);
  }
}

export function flush(useBeacon = false) {
  if (!ENABLED || buffer.length === 0 || !SUPABASE_URL || !ANON_KEY) return;
  const events = buffer.splice(0, buffer.length);
  const url = `${SUPABASE_URL}/functions/v1/ingest-metrics`;
  const body = JSON.stringify({ events, account_id: accountId, app_version: APP_VERSION });

  // Pas de navigator.sendBeacon : il envoie toujours les cookies (credentials
  // "include"), ce que la réponse CORS de la fonction (`Allow-Origin: *`)
  // interdit — les événements envoyés à la fermeture de page étaient perdus.
  // fetch + keepalive survit aussi au déchargement de la page.
  void useBeacon;
  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: ANON_KEY,
      Authorization: `Bearer ${ANON_KEY}`,
    },
    body,
    keepalive: true,
  }).catch(() => {
    /* swallow */
  });
}

if (typeof window !== "undefined" && ENABLED) {
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
}
